/**
 * ResMan-specific pure helpers. ResMan's setup is form-driven (no file upload, no
 * API), so these helpers are simpler than AppFolio's. The form collects four
 * structure-level values plus a per-unit name mapping; helpers below produce the
 * persistable shapes from form state.
 */

import type { ResmanConfig } from '@/Structures/types';
import type { Unit, UnitPmsConfig } from '@/Units/types';

/** Form state for the structure-level ResMan config. */
export interface ResmanFormState {
  propertyShortName: string;
  defaultTransactionCategory: string;
  defaultPaymentType: string;
  defaultSplitLedger: string;
}

/** Build the structure-level pms_config from the form state, dropping empty
 * optional fields so we don't persist whitespace-only values. */
export function buildResmanStructureConfig(state: ResmanFormState): ResmanConfig {
  const trimmedPropertyShortName = state.propertyShortName.trim();
  const trimmedCategory = state.defaultTransactionCategory.trim();
  const trimmedPaymentType = state.defaultPaymentType.trim();
  const trimmedSplitLedger = state.defaultSplitLedger.trim();
  return {
    property_short_name: trimmedPropertyShortName,
    ...(trimmedCategory && { default_transaction_category: trimmedCategory }),
    ...(trimmedPaymentType && { default_payment_type: trimmedPaymentType }),
    ...(trimmedSplitLedger && { default_split_ledger: trimmedSplitLedger }),
  };
}

/** Build the per-unit pms_config from the user-entered ResMan unit name. */
export function buildResmanUnitConfig(unitName: string): UnitPmsConfig {
  return { unit_name: unitName.trim() };
}

/** Validate the ResMan structure form before save. Returns an error message
 * suitable for a toast, or null if the form is valid. */
export function validateResmanFormState(state: ResmanFormState): string | null {
  if (!state.propertyShortName.trim()) {
    return 'Property Name is required';
  }
  return null;
}

/**
 * Build the editable unit-mappings table state for the FIRST-TIME setup flow,
 * where no `pmsConfig` exists yet. Defaults to the SimpleSUB unit name for every
 * unit so a user who skims past the mapping table still ends up with usable
 * persisted mappings. Use only when no save has happened yet.
 */
export function buildResmanInitialUnitMappings(billableUnits: Unit[]): Record<string, string> {
  const mappings: Record<string, string> = {};
  for (const u of billableUnits) {
    mappings[u.id] = u.pms_config?.unit_name ?? u.name;
  }
  return mappings;
}

/**
 * Build the editable unit-mappings table state for the EDIT flow on an already-
 * configured property. Shows only persisted `pms_config.unit_name` values; units
 * that the user previously cleared (or never mapped) stay blank, so the form
 * accurately reflects what the customer can verify in the read-only view. The
 * user can still manually type a ResMan name per row.
 */
export function buildResmanPersistedUnitMappings(billableUnits: Unit[]): Record<string, string> {
  const mappings: Record<string, string> = {};
  for (const u of billableUnits) {
    if (u.pms_config?.unit_name) {
      mappings[u.id] = u.pms_config.unit_name;
    }
  }
  return mappings;
}
